import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Plus, X, Trash2, Eye, Edit, Star, Search, Check, Users, Filter, ChevronDown, ChevronUp } from "lucide-react";

import {
    createCampagneRequest,
    deleteCampagneRequest,
    fetchCampagnesRequest,
    updateCampagneRequest,
} from "../../../redux/actions/campagnesActions";
import { getAllEntitesRequest } from "../../../redux/actions/entitesActions";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { getAllJobsRequest } from "../../../redux/actions/Jobactions";
import MultiSelectDropdown from "../../../Ui/UiJs/MultiSelectDropdown";
import "./CampagneCompetences.css";

/* ─────────────────────── helpers ─────────────────────── */
const toDateInputValue = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "" : dt.toISOString().split("T")[0];
};

const uniqNums = (arr) =>
    Array.from(new Set((arr || []).map((x) => Number(x)).filter((x) => !Number.isNaN(x))));

const splitAxes = (s) =>
    s ? String(s).split("\n").map((x) => x.trim()).filter(Boolean) : [];

const formatDateFR = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("fr-FR");
};

const EMPTY_FORM = {
    nom: "", reference: "", description: "",
    dateDebut: "", dateFin: "",
    anneeCampagne: new Date().getFullYear(),
    niveauValidation: "MANAGER",
    envoiEmails: "INSTANTANE", dateEnvoiEmails: "",
    planificationRelance: "false",
    dateRelance: "", dateRelance2: "", dateRelance3: "",
    isDefault: false,
    activationPurpose: "",
    activationDateDebut: "",
    activationDateFin: "",
    status: true,
};

/* ─────────────────────── InlineUsersPanel ─────────────────────── */
function InlineUsersPanel({ users, usersState, setUsersState, setSelectedUsers, entiteIds, posteIds }) {
    const [search, setSearch] = useState("");
    const [autoFilter, setAutoFilter] = useState(false);

    const AVATAR_COLORS = ["#204385", "#ee645f", "#0891b2", "#059669", "#7c3aed", "#d97706"];
    const getAvatarColor = (id) => AVATAR_COLORS[+id % AVATAR_COLORS.length];
    const getInitials = (u) => {
        const first = (u.firstName || u.prenom || "?")[0];
        const last = (u.lastName || u.nom || "")[0] || "";
        return `${first}${last}`.toUpperCase();
    };
    const getFullName = (u) =>
        `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""}`.trim() || `User #${u.id}`;

    const baseList = useMemo(() => {
        if (!autoFilter || (!entiteIds.length && !posteIds.length)) return usersState || [];
        return (usersState || []).filter(u => {
            const matchEnt = entiteIds.length === 0 || entiteIds.includes(+u.entiteId) || entiteIds.includes(+u.entityId);
            const matchPost = posteIds.length === 0 || posteIds.includes(+u.posteId) || posteIds.includes(+u.jobId);
            return matchEnt && matchPost;
        });
    }, [usersState, autoFilter, entiteIds, posteIds]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return baseList;
        return baseList.filter(u =>
            `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""} ${u.email || ""}`.toLowerCase().includes(q)
        );
    }, [baseList, search]);

    const toggleUser = (userId) => {
        const updated = usersState.map(u => +u.id === +userId ? { ...u, checked: !u.checked } : u);
        setUsersState(updated);
        setSelectedUsers(updated.filter(u => u.checked));
    };

    const toggleAll = () => {
        const allChecked = filtered.every(u => u.checked);
        const filteredIds = new Set(filtered.map(u => +u.id));
        const updated = usersState.map(u => filteredIds.has(+u.id) ? { ...u, checked: !allChecked } : u);
        setUsersState(updated);
        setSelectedUsers(updated.filter(u => u.checked));
    };

    const clearAll = () => {
        const updated = usersState.map(u => ({ ...u, checked: false }));
        setUsersState(updated);
        setSelectedUsers([]);
    };

    const checkedUsers = (usersState || []).filter(u => u.checked);
    const allFilteredChecked = filtered.length > 0 && filtered.every(u => u.checked);
    const hasAutoFilterOption = entiteIds.length > 0 || posteIds.length > 0;

    return (
        <div className="sk2-users-panel">
            <div className="sk2-users-panel__header">
                <div className="sk2-users-panel__title"><Users size={14} /> Collaborateurs</div>
                <div className="sk2-users-header-right">
                    {hasAutoFilterOption && (
                        <button type="button" className={`sk2-auto-filter-btn${autoFilter ? " active" : ""}`} onClick={() => setAutoFilter(p => !p)}>
                            <Filter size={12} /> Filtrer par entités/postes
                        </button>
                    )}
                    <div className="sk2-users-search">
                        <Search size={12} className="sk2-users-search-icon" />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="sk2-users-search-input" />
                    </div>
                </div>
            </div>

            {checkedUsers.length > 0 && (
                <div className="sk2-users-summary">
                    <div className="sk2-avatar-stack">
                        {checkedUsers.slice(0, 5).map(u => (
                            <div key={u.id} className="sk2-avatar-stack__item" style={{ background: getAvatarColor(u.id) }} title={getFullName(u)}>
                                {getInitials(u)}
                            </div>
                        ))}
                        {checkedUsers.length > 5 && <div className="sk2-avatar-stack__more">+{checkedUsers.length - 5}</div>}
                    </div>
                    <span className="sk2-users-summary__label"><strong>{checkedUsers.length}</strong> collaborateur{checkedUsers.length > 1 ? "s" : ""} sélectionné{checkedUsers.length > 1 ? "s" : ""}</span>
                    <button type="button" className="sk2-users-summary__clear" onClick={clearAll}><X size={11} /> Tout désélectionner</button>
                </div>
            )}

            {(usersState || []).length === 0 ? (
                <div className="sk2-users-empty"><Users size={28} style={{ color: "#d1d5db", marginBottom: 8 }} /><div>Aucun utilisateur disponible</div></div>
            ) : filtered.length === 0 ? (
                <div className="sk2-users-empty">Aucun résultat{search ? ` pour « ${search} »` : " avec ce filtre"}</div>
            ) : (
                <>
                    <div className="sk2-users-select-all" onClick={toggleAll}>
                        <div className={`sk2-user-checkbox${allFilteredChecked ? " checked" : ""}`}>{allFilteredChecked && <Check size={10} />}</div>
                        <span className="sk2-users-select-text">{allFilteredChecked ? "Tout désélectionner" : "Tout sélectionner"} ({filtered.length})</span>
                    </div>
                    <div className="sk2-users-list">
                        {filtered.map(u => (
                            <div key={u.id} className={`sk2-user-row${u.checked ? " checked" : ""}`} onClick={() => toggleUser(u.id)}>
                                <div className={`sk2-user-checkbox${u.checked ? " checked" : ""}`}>{u.checked && <Check size={10} />}</div>
                                <div className="sk2-user-avatar" style={u.checked ? { background: getAvatarColor(u.id), color: "#fff" } : {}}>{getInitials(u)}</div>
                                <div className="sk2-user-info">
                                    <div className="sk2-user-name">{getFullName(u)}</div>
                                    {u.email && <div className="sk2-user-email">{u.email}</div>}
                                </div>
                                {u.poste && <span className="sk2-badge sk2-badge--grey" style={{ fontSize: 10, marginLeft: "auto" }}>{u.poste}</span>}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

/* ─────────────────────── ViewModal ─────────────────────── */
function ViewModal({ open, campagne, onClose, entites }) {
    if (!open || !campagne) return null;
    const getEntName = (id) => (entites || []).find(e => +e.id === +id)?.name || `#${id}`;
    const formatAxes = (a) => a ? String(a).split("\n").filter(Boolean).join(", ") : "—";

    return (
        <div className="sk2-overlay" onClick={onClose}>
            <div className="sk2-modal" onClick={e => e.stopPropagation()} role="dialog">
                <div className="sk2-modal__header">
                    <div className="sk2-modal__title">
                        <Star size={16} style={{ color: "#ee645f" }} /> 
                        Détails — {campagne.nom}
                        {!!campagne.isDefault && <span className="sk2-badge sk2-badge--orange" style={{ marginLeft: 8, fontSize: 10 }}>DÉFAUT</span>}
                    </div>
                    <button className="sk2-icon-btn" onClick={onClose} type="button"><X size={16} /></button>
                </div>
                <div className="sk2-modal__body">
                    <div className="sk2-view-section">
                        <h3 className="sk2-view-title">Informations générales</h3>
                        <div className="sk2-view-grid">
                            <div className="sk2-view-item"><span className="sk2-view-label">Nom</span><span className="sk2-view-value sk2-strong">{campagne.nom}</span></div>
                            <div className="sk2-view-item"><span className="sk2-view-label">Référence</span><span className="sk2-view-value sk2-muted">{campagne.reference || "—"}</span></div>
                            <div className="sk2-view-item"><span className="sk2-view-label">Année</span><span className="sk2-badge sk2-badge--blue">{campagne.anneeCampagne}</span></div>
                            <div className="sk2-view-item"><span className="sk2-view-label">Statut</span>{campagne.status ? <span className="sk2-badge sk2-badge--green">Active</span> : <span className="sk2-badge sk2-badge--grey">Inactive</span>}</div>
                            {campagne.description && <div className="sk2-view-item sk2-view-item--full"><span className="sk2-view-label">Description</span><span className="sk2-view-value sk2-muted">{campagne.description}</span></div>}
                            {campagne.axes && <div className="sk2-view-item sk2-view-item--full"><span className="sk2-view-label">Axes de compétences</span><span className="sk2-view-value sk2-muted">{formatAxes(campagne.axes)}</span></div>}
                        </div>
                    </div>
                    <div className="sk2-view-section">
                        <h3 className="sk2-view-title">Traitement d'activation</h3>
                        <div className="sk2-view-grid">
                            <div className="sk2-view-item sk2-view-item--full"><span className="sk2-view-label">Objet</span><span className="sk2-view-value">{campagne.activationPurpose || "Non spécifié"}</span></div>
                            <div className="sk2-view-item"><span className="sk2-view-label">Début Activation</span><span className="sk2-view-value">{formatDateFR(campagne.activationDateDebut)}</span></div>
                            <div className="sk2-view-item"><span className="sk2-view-label">Fin Activation</span><span className="sk2-view-value">{formatDateFR(campagne.activationDateFin)}</span></div>
                            <div className="sk2-view-item"><span className="sk2-view-label">Exceptions</span><span className="sk2-view-value">{campagne.activationUserIds?.length || 0} collaborateur(s)</span></div>
                        </div>
                    </div>
                    <div className="sk2-view-section">
                        <h3 className="sk2-view-title">Dates Campagne</h3>
                        <div className="sk2-view-grid">
                            <div className="sk2-view-item"><span className="sk2-view-label">Début</span><span className="sk2-view-value">{formatDateFR(campagne.dateDebut)}</span></div>
                            <div className="sk2-view-item"><span className="sk2-view-label">Fin</span><span className="sk2-view-value">{formatDateFR(campagne.dateFin)}</span></div>
                        </div>
                    </div>
                    <div className="sk2-view-section">
                        <h3 className="sk2-view-title">Configuration</h3>
                        <div className="sk2-view-grid">
                            <div className="sk2-view-item"><span className="sk2-view-label">Validation</span><span className="sk2-badge sk2-badge--blue">{campagne.validation || "—"}</span></div>
                            <div className="sk2-view-item"><span className="sk2-view-label">Emails</span><span className="sk2-badge sk2-badge--blue">{campagne.envoiEmails || "—"}</span></div>
                            <div className="sk2-view-item"><span className="sk2-view-label">Filter mode</span><span className="sk2-badge sk2-badge--grey">{campagne.filterMode || "—"}</span></div>
                        </div>
                    </div>
                    {campagne.entiteIds?.length > 0 && (
                        <div className="sk2-view-section">
                            <h3 className="sk2-view-title">Entités ciblées</h3>
                            <div className="sk2-tag-list">{campagne.entiteIds.map(id => <span key={id} className="sk2-badge sk2-badge--grey">{getEntName(id)}</span>)}</div>
                        </div>
                    )}
                </div>
                <div className="sk2-modal__footer">
                    <button className="sk2-btn sk2-btn--ghost" type="button" onClick={onClose}>Fermer</button>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────── FormModal ─────────────────────── */
function FormModal({ open, campagneId, campagnes, onClose, onSave, isLoading, entites, jobs, users }) {
    const campagne = useMemo(() =>
            campagneId ? (campagnes || []).find(c => +c.id === +campagneId) || null : null,
        [campagneId, campagnes]
    );

    const [form, setForm] = useState(EMPTY_FORM);
    const [filterMode, setFilterMode] = useState("IN");
    const [managerLevelFilter, setManagerLevelFilter] = useState("");
    const [entiteIds, setEntiteIds] = useState([]);
    const [posteIds, setPosteIds] = useState([]);
    const [usersState, setUsersState] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [initialUserIds, setInitialUserIds] = useState([]);
    const [axeToAdd, setAxeToAdd] = useState("");
    const [axesArray, setAxesArray] = useState([]);
    const [errors, setErrors] = useState({});
    const [showUsersPanel, setShowUsersPanel] = useState(false);
    const [showActivationUsersPanel, setShowActivationUsersPanel] = useState(false);
    const [activationUsersState, setActivationUsersState] = useState([]);
    const [selectedActivationUsers, setSelectedActivationUsers] = useState([]);
    const [initialActivationUserIds, setInitialActivationUserIds] = useState([]);
    const [activeSection, setActiveSection] = useState("identification");

    useEffect(() => {
        if (!open) return;
        if (!campagne) {
            setForm(EMPTY_FORM);
            setFilterMode("IN"); setManagerLevelFilter(""); setEntiteIds([]); setPosteIds([]);
            setUsersState([]); setSelectedUsers([]); setInitialUserIds([]);
            setActivationUsersState([]); setSelectedActivationUsers([]); setInitialActivationUserIds([]);
            setAxeToAdd(""); setAxesArray([]); setErrors({}); setActiveSection("identification");
            setShowUsersPanel(false); setShowActivationUsersPanel(false);
            return;
        }
        setForm({
            nom: campagne.nom || "", reference: campagne.reference || "",
            description: campagne.description || "",
            dateDebut: toDateInputValue(campagne.dateDebut),
            dateFin: toDateInputValue(campagne.dateFin),
            anneeCampagne: campagne.anneeCampagne || new Date().getFullYear(),
            niveauValidation: campagne.validation || campagne.niveauValidation || "MANAGER",
            envoiEmails: campagne.envoiEmails || "INSTANTANE",
            dateEnvoiEmails: toDateInputValue(campagne.dateEnvoiEmails),
            planificationRelance: String(campagne.planificationRelance ?? "false"),
            dateRelance: toDateInputValue(campagne.dateRelance),
            dateRelance2: toDateInputValue(campagne.dateRelance2),
            dateRelance3: toDateInputValue(campagne.dateRelance3),
            isDefault: !!campagne.isDefault,
            activationPurpose: campagne.activationPurpose || "",
            activationDateDebut: toDateInputValue(campagne.activationDateDebut),
            activationDateFin: toDateInputValue(campagne.activationDateFin),
            status: Boolean(campagne.status ?? true),
        });
        setFilterMode(campagne.filterMode || "IN");
        setManagerLevelFilter(campagne.managerLevelFilter || "");
        setEntiteIds(uniqNums(campagne.entiteIds || []));
        setPosteIds(uniqNums(campagne.posteIds || []));
        setInitialUserIds(uniqNums(campagne.userIds || []));
        setInitialActivationUserIds(uniqNums(campagne.activationUserIds || []));
        setAxesArray(splitAxes(campagne.axes));
        setErrors({}); setActiveSection("identification"); setShowUsersPanel(false); setShowActivationUsersPanel(false);
    }, [open, campagne]);

    useEffect(() => {
        const prevMap = new Map((usersState || []).map(u => [+u.id, !!u.checked]));
        const initSet = new Set((initialUserIds || []).map(x => +x));
        const next = (users || []).map(u => ({ ...u, checked: prevMap.has(+u.id) ? prevMap.get(+u.id) : initSet.has(+u.id) }));
        setUsersState(next);
        setSelectedUsers(next.filter(u => u.checked));

        const prevActMap = new Map((activationUsersState || []).map(u => [+u.id, !!u.checked]));
        const initActSet = new Set((initialActivationUserIds || []).map(x => +x));
        const nextAct = (users || []).map(u => ({ ...u, checked: prevActMap.has(+u.id) ? prevActMap.get(+u.id) : initActSet.has(+u.id) }));
        setActivationUsersState(nextAct);
        setSelectedActivationUsers(nextAct.filter(u => u.checked));
    }, [users, initialUserIds, initialActivationUserIds]);

    const validate = () => {
        const e = {};
        if (!form.nom.trim()) e.nom = "Obligatoire";
        if (!form.description.trim()) e.description = "Obligatoire";
        if (form.envoiEmails === "DIFFERE" && !form.dateEnvoiEmails) e.dateEnvoiEmails = "Date obligatoire";
        if (form.planificationRelance === "true" && !form.dateRelance) e.dateRelance = "Date relance 1 obligatoire";
        setErrors(e);
        return !Object.keys(e).length;
    };

    const buildPayload = () => ({
        nom: form.nom, reference: form.reference, description: form.description,
        dateDebut: form.dateDebut || null, dateFin: form.dateFin || null,
        anneeCampagne: +form.anneeCampagne,
        typeCampagne: "SKILLS",
        filterMode, managerLevelFilter: managerLevelFilter || null,
        entiteIds: uniqNums(entiteIds), posteIds: uniqNums(posteIds),
        userIds: uniqNums(selectedUsers.map(u => u.id)),
        catalogueIds: [], nombreMaxFormations: 1, recruitmentLimits: [],
        validation: form.niveauValidation,
        envoiEmails: form.envoiEmails,
        dateEnvoiEmails: form.envoiEmails === "DIFFERE" ? form.dateEnvoiEmails || null : null,
        planificationRelance: form.planificationRelance === "true",
        dateRelance: form.planificationRelance === "true" ? form.dateRelance || null : null,
        dateRelance2: form.planificationRelance === "true" ? form.dateRelance2 || null : null,
        dateRelance3: form.planificationRelance === "true" ? form.dateRelance3 || null : null,
        axes: axesArray.join("\n"),
        status: Boolean(form.status),
        isDefault: !!form.isDefault,
        activationPurpose: form.activationPurpose,
        activationDateDebut: form.activationDateDebut || null,
        activationDateFin: form.activationDateFin || null,
        activationUserIds: uniqNums(selectedActivationUsers.map(u => u.id)),
        budgets: [],
    });

    const handleSubmit = () => { if (!validate()) return; onSave(campagneId, buildPayload()); };

    const addAxe = () => {
        const v = axeToAdd.trim();
        if (!v || axesArray.includes(v)) { setAxeToAdd(""); return; }
        setAxesArray(p => [...p, v]); setAxeToAdd("");
    };

    const sf = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

    const sections = [
        { id: "identification", label: "Identification" },
        { id: "population", label: "Population" },
        { id: "activation", label: "Activation" },
        { id: "validation", label: "Validation" },
        { id: "relances", label: "Relances" },
        { id: "axes", label: "Axes" },
        { id: "autres", label: "Autres" },
    ];

    const sectionHasError = (id) => {
        if (id === "identification") return !!(errors.nom || errors.description);
        if (id === "relances") return !!errors.dateRelance;
        if (id === "autres") return !!errors.dateEnvoiEmails;
        return false;
    };

    if (!open) return null;

    return (
        <div className="sk2-overlay" style={{ zIndex: 9998 }}>
            <div className="sk2-modal sk2-modal--wide" role="dialog">
                <div className="sk2-modal__header">
                    <div className="sk2-modal__title">
                        <Star size={16} style={{ color: "#ee645f" }} />
                        {campagneId ? "Modifier la campagne Compétences" : "Créer une campagne Compétences"}
                    </div>
                    <button className="sk2-icon-btn" onClick={onClose} type="button"><X size={16} /></button>
                </div>

                {/* Section nav */}
                <div className="sk2-section-nav">
                    {sections.map(s => (
                        <button key={s.id} type="button"
                                className={`sk2-section-tab${activeSection === s.id ? " active" : ""}${sectionHasError(s.id) ? " has-error" : ""}`}
                                onClick={() => setActiveSection(s.id)}>
                            {s.label}
                            {sectionHasError(s.id) && <span className="sk2-tab-dot" />}
                        </button>
                    ))}
                </div>

                <div className="sk2-modal__body sk2-form-body">

                    {/* ── Identification ── */}
                    {activeSection === "identification" && (
                        <div className="sk2-form-section">
                            <h3 className="sk2-form-title">Identification</h3>
                            <div className="sk2-form-grid">
                                <div className="sk2-field">
                                    <label>Nom *</label>
                                    <input value={form.nom} onChange={sf("nom")} placeholder="Ex: Campagne Compétences 2025" />
                                    {errors.nom && <div className="sk2-error">{errors.nom}</div>}
                                </div>
                                <div className="sk2-field">
                                    <label>Référence</label>
                                    <input value={form.reference} onChange={sf("reference")} placeholder="Ex: CPG-SK-2025-001" />
                                </div>
                                <div className="sk2-field sk2-field--full">
                                    <label>Description *</label>
                                    <textarea value={form.description} onChange={sf("description")} rows={3} placeholder="Description de la campagne de compétences..." />
                                    {errors.description && <div className="sk2-error">{errors.description}</div>}
                                </div>
                                <div className="sk2-field">
                                    <label>Date début</label>
                                    <input type="date" value={form.dateDebut} onChange={sf("dateDebut")} />
                                </div>
                                <div className="sk2-field">
                                    <label>Date de fin</label>
                                    <input type="date" value={form.dateFin} onChange={sf("dateFin")} />
                                </div>
                                <div className="sk2-field">
                                    <label>Année campagne</label>
                                    <input type="number" value={form.anneeCampagne} onChange={sf("anneeCampagne")} />
                                </div>
                                <div className="sk2-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px', paddingTop: '28px' }}>
                                    <input 
                                        type="checkbox" 
                                        id="isDefault"
                                        checked={form.isDefault} 
                                        onChange={e => setForm(p => ({ ...p, isDefault: e.target.checked }))} 
                                        style={{ width: 'auto' }}
                                    />
                                    <label htmlFor="isDefault" style={{ cursor: 'pointer' }}>Utiliser comme campagne par défaut</label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Population ── */}
                    {activeSection === "population" && (
                        <div className="sk2-form-section">
                            <h3 className="sk2-form-title">Population cible</h3>

                            {/* Filtres inline pill-style */}
                            <div className="sk2-filters-inline">
                                <div className="sk2-filter-group">
                                    <span className="sk2-filter-group__label">Mode de filtrage</span>
                                    <div className="sk2-filter-group__options">
                                        {[{ value: "IN", label: "IN" }, { value: "NOT_IN", label: "NOT IN" }].map(opt => (
                                            <label key={opt.value} className="sk2-pill-radio">
                                                <input type="radio" name="filterMode" value={opt.value} checked={filterMode === opt.value} onChange={() => setFilterMode(opt.value)} />
                                                <span className={`sk2-pill${filterMode === opt.value ? " active" : ""}`}>{opt.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="sk2-filters-divider" />
                                <div className="sk2-filter-group">
                                    <span className="sk2-filter-group__label">Niveau manager</span>
                                    <div className="sk2-filter-group__options">
                                        {[{ value: "", label: "Aucun" }, { value: "n+1", label: "N+1" }, { value: "n+2", label: "N+2" }].map(opt => (
                                            <label key={opt.value} className="sk2-pill-radio">
                                                <input type="radio" name="managerLevel" value={opt.value} checked={managerLevelFilter === opt.value} onChange={() => setManagerLevelFilter(opt.value)} />
                                                <span className={`sk2-pill${managerLevelFilter === opt.value ? " active" : ""}`}>{opt.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Entités & Postes */}
                            <div className="sk2-form-grid" style={{ marginTop: 14 }}>
                                <div className="sk2-field">
                                    <MultiSelectDropdown label="Entités" items={entites || []} value={entiteIds} onChange={setEntiteIds}
                                                         placeholder={entiteIds.length > 0 ? `${entiteIds.length} sélectionnée(s)` : "Sélectionner..."}
                                                         getId={x => +x.id} getLabel={x => x.nom || x.name || `#${x.id}`} />
                                    {entiteIds.length > 0 && (
                                        <div className="sk2-selected-tags">
                                            {entiteIds.map(id => {
                                                const ent = (entites || []).find(e => +e.id === id);
                                                return (
                                                    <span key={id} className="sk2-selected-tag">
                                                        {ent ? (ent.nom || ent.name) : `#${id}`}
                                                        <button type="button" onClick={() => setEntiteIds(p => p.filter(x => x !== id))}><X size={9} /></button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="sk2-field">
                                    <MultiSelectDropdown label="Postes" items={jobs || []} value={posteIds} onChange={setPosteIds}
                                                         placeholder={posteIds.length > 0 ? `${posteIds.length} sélectionné(s)` : "Sélectionner..."}
                                                         getId={x => +x.id} getLabel={x => x.nom || x.name || `#${x.id}`} />
                                    {posteIds.length > 0 && (
                                        <div className="sk2-selected-tags">
                                            {posteIds.map(id => {
                                                const job = (jobs || []).find(j => +j.id === id);
                                                return (
                                                    <span key={id} className="sk2-selected-tag">
                                                        {job ? (job.nom || job.name) : `#${id}`}
                                                        <button type="button" onClick={() => setPosteIds(p => p.filter(x => x !== id))}><X size={9} /></button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Collaborateurs toggle */}
                            <div className="sk2-collab-toggle" style={{ marginTop: 16 }}>
                                <div className="sk2-collab-toggle__info">
                                    <div className="sk2-strong" style={{ fontSize: 12 }}>Collaborateurs</div>
                                    <div className="sk2-hint">
                                        {selectedUsers.length > 0
                                            ? `${selectedUsers.length} collaborateur${selectedUsers.length > 1 ? "s" : ""} sélectionné${selectedUsers.length > 1 ? "s" : ""}`
                                            : "Aucun collaborateur sélectionné"}
                                    </div>
                                </div>
                                <button type="button" className={`sk2-btn${showUsersPanel ? " sk2-btn--ghost" : " sk2-btn--primary"}`} onClick={() => setShowUsersPanel(p => !p)}>
                                    <Users size={14} />
                                    {showUsersPanel ? "Masquer" : "Afficher les collaborateurs"}
                                    {showUsersPanel ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                </button>
                            </div>

                            {showUsersPanel && (
                                <InlineUsersPanel users={users || []} usersState={usersState} setUsersState={setUsersState}
                                                  setSelectedUsers={setSelectedUsers} filterMode={filterMode}
                                                  managerLevelFilter={managerLevelFilter} entiteIds={entiteIds} posteIds={posteIds} />
                            )}
                        </div>
                    )}

                    {/* ── Activation ── */}
                    {activeSection === "activation" && (
                        <div className="sk2-form-section">
                            <h3 className="sk2-form-title">Ciblage d'activation</h3>
                            <div className="sk2-form-grid">
                                <div className="sk2-field sk2-field--full">
                                    <label>Objet / Cause de l'activation</label>
                                    <input value={form.activationPurpose} onChange={sf("activationPurpose")} placeholder="Ex: Activation annuelle, Campagne spécifique..." />
                                </div>
                                <div className="sk2-field">
                                    <label>Date début activation</label>
                                    <input type="date" value={form.activationDateDebut} onChange={sf("activationDateDebut")} />
                                </div>
                                <div className="sk2-field">
                                    <label>Date de fin activation</label>
                                    <input type="date" value={form.activationDateFin} onChange={sf("activationDateFin")} />
                                </div>
                            </div>

                            <div className="sk2-collab-toggle" style={{ marginTop: 16 }}>
                                <div className="sk2-collab-toggle__info">
                                    <div className="sk2-strong" style={{ fontSize: 12 }}>Collaborateurs (Activation)</div>
                                    <div className="sk2-hint">
                                        {selectedActivationUsers.length > 0
                                            ? `${selectedActivationUsers.length} collaborateur(s) sélectionné(s)`
                                            : "Par défaut : tous les collaborateurs concernés par la campagne"}
                                    </div>
                                </div>
                                <button type="button" className={`sk2-btn${showActivationUsersPanel ? " sk2-btn--ghost" : " sk2-btn--primary"}`} onClick={() => setShowActivationUsersPanel(p => !p)}>
                                    <Users size={14} />
                                    {showActivationUsersPanel ? "Masquer" : "Gérer les exceptions"}
                                    {showActivationUsersPanel ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                </button>
                            </div>

                            {showActivationUsersPanel && (
                                <InlineUsersPanel 
                                    users={users || []} 
                                    usersState={activationUsersState} 
                                    setUsersState={setActivationUsersState}
                                    setSelectedUsers={setSelectedActivationUsers} 
                                    entiteIds={entiteIds} 
                                    posteIds={posteIds} 
                                />
                            )}
                        </div>
                    )}

                    {/* ── Validation ── */}
                    {activeSection === "validation" && (
                        <div className="sk2-form-section">
                            <h3 className="sk2-form-title">Niveau de validation</h3>
                            <div className="sk2-form-grid">
                                <div className="sk2-field">
                                    <label>Niveau de validation</label>
                                    <select value={form.niveauValidation} onChange={sf("niveauValidation")}>
                                        <option value="MANAGER">Manager N+1 uniquement</option>
                                        <option value="N2">Manager N+2 uniquement</option>
                                        <option value="BOTH">Manager N+1 et Manager N+2</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Relances ── */}
                    {activeSection === "relances" && (
                        <div className="sk2-form-section">
                            <h3 className="sk2-form-title">Relances planifiées</h3>
                            <div className="sk2-form-grid">
                                <div className="sk2-field">
                                    <label>Relances planifiées</label>
                                    <select value={form.planificationRelance} onChange={sf("planificationRelance")}>
                                        <option value="false">Non</option>
                                        <option value="true">Oui</option>
                                    </select>
                                </div>
                            </div>
                            {form.planificationRelance === "true" && (
                                <div className="sk2-form-grid" style={{ marginTop: 12 }}>
                                    <div className="sk2-field">
                                        <label>Date relance 1 *</label>
                                        <input type="date" value={form.dateRelance} onChange={sf("dateRelance")} />
                                        {errors.dateRelance && <div className="sk2-error">{errors.dateRelance}</div>}
                                    </div>
                                    <div className="sk2-field">
                                        <label>Date relance 2</label>
                                        <input type="date" value={form.dateRelance2} onChange={sf("dateRelance2")} />
                                    </div>
                                    <div className="sk2-field">
                                        <label>Date relance 3</label>
                                        <input type="date" value={form.dateRelance3} onChange={sf("dateRelance3")} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Axes ── */}
                    {activeSection === "axes" && (
                        <div className="sk2-form-section">
                            <h3 className="sk2-form-title">Axes de compétences</h3>
                            <div className="sk2-form-grid">
                                <div className="sk2-field sk2-field--full">
                                    <label>Ajouter un axe</label>
                                    <div style={{ display: "flex", gap: 10 }}>
                                        <input value={axeToAdd} onChange={e => setAxeToAdd(e.target.value)}
                                               onKeyDown={e => e.key === "Enter" && addAxe()}
                                               placeholder="Ex: Communication, Analytique..." style={{ flex: 1 }} />
                                        <button type="button" className="sk2-btn sk2-btn--primary" onClick={addAxe}>+ Ajouter</button>
                                    </div>
                                </div>
                            </div>
                            <div className="sk2-axes-list">
                                {axesArray.length === 0
                                    ? <div className="sk2-hint">Aucun axe ajouté.</div>
                                    : axesArray.map((axe, idx) => (
                                        <div key={idx} className="sk2-axe-row">
                                            <span className="sk2-badge sk2-badge--blue">{axe}</span>
                                            <button type="button" className="sk2-icon-btn sk2-icon-btn--danger" onClick={() => setAxesArray(p => p.filter((_, i) => i !== idx))}>
                                                <X size={13} />
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* ── Autres ── */}
                    {activeSection === "autres" && (
                        <div className="sk2-form-section">
                            <h3 className="sk2-form-title">Paramètres email & statut</h3>
                            <div className="sk2-form-grid">
                                <div className="sk2-field">
                                    <label>Envoi emails</label>
                                    <select value={form.envoiEmails} onChange={sf("envoiEmails")}>
                                        <option value="INSTANTANE">Instantané</option>
                                        <option value="DIFFERE">Différé</option>
                                        <option value="SANS_EMAIL">Sans email</option>
                                    </select>
                                </div>
                                <div className="sk2-field">
                                    <label>Statut</label>
                                    <select value={String(form.status)} onChange={e => setForm(p => ({ ...p, status: e.target.value === "true" }))}>
                                        <option value="true">Actif</option>
                                        <option value="false">Inactif</option>
                                    </select>
                                </div>
                                {form.envoiEmails === "DIFFERE" && (
                                    <div className="sk2-field">
                                        <label>Date envoi emails *</label>
                                        <input type="date" value={form.dateEnvoiEmails} onChange={sf("dateEnvoiEmails")} />
                                        {errors.dateEnvoiEmails && <div className="sk2-error">{errors.dateEnvoiEmails}</div>}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="sk2-modal__footer">
                    <button className="sk2-btn sk2-btn--ghost" type="button" onClick={onClose} disabled={isLoading}>Annuler</button>
                    <button className="sk2-btn sk2-btn--primary" type="button" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? "Enregistrement..." : campagneId ? "Enregistrer" : "Créer la campagne"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────── Main ─────────────────────── */
export default function CampagneCompetences() {
    const dispatch = useDispatch();

    const campagnesState = useSelector(s => s.campagnes || {});
    const campagnes = campagnesState.list || [];
    const campagnesLoading = !!campagnesState.loading;

    const jobs = useSelector(s => s.jobs?.jobs || []);
    const entites = useSelector(s => s.entite?.entites || []);
    const users = useSelector(s => s.user?.users || s.users?.users || []);

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [selectedCampagne, setSelectedCampagne] = useState(null);
    const [tableQ, setTableQ] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        dispatch(getAllJobsRequest());
        dispatch(getAllEntitesRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch]);

    const openAdd = () => { setEditId(null); setFormModalOpen(true); };
    const openEdit = (c) => { setEditId(c.id); setFormModalOpen(true); };
    const openView = (c) => { setSelectedCampagne(c); setViewModalOpen(true); };

    const handleSave = useCallback((cid, payload) => {
        if (cid) dispatch(updateCampagneRequest(cid, payload));
        else dispatch(createCampagneRequest(payload));
        Swal.fire({ icon: "success", text: cid ? "Campagne modifiée" : "Campagne créée", timer: 1200, showConfirmButton: false });
        setFormModalOpen(false);
    }, [dispatch]);

    const remove = async (c) => {
        const r = await Swal.fire({
            icon: "warning", title: "Supprimer ?", text: c.nom,
            showCancelButton: true, confirmButtonColor: "#ee645f",
            cancelButtonColor: "#204385", confirmButtonText: "Supprimer", cancelButtonText: "Annuler"
        });
        if (r.isConfirmed) dispatch(deleteCampagneRequest(c.id));
    };

    const filteredCampagnes = useMemo(() => {
        const q = tableQ.trim().toLowerCase();
        const byType = (campagnes || []).filter(c => String(c.typeCampagne || "").toUpperCase() === "SKILLS");
        const byStatus = filterStatus === "all" ? byType :
            filterStatus === "active" ? byType.filter(c => !!c.status) :
                byType.filter(c => !c.status);
        if (!q) return byStatus;
        return byStatus.filter(c => `${c.nom || ""} ${c.reference || ""} ${c.anneeCampagne || ""}`.toLowerCase().includes(q));
    }, [campagnes, tableQ, filterStatus]);

    return (
        <div className="sk2-page">
            {/* ── Header ── */}
            <div className="sk2-header">
                <div>
                    <h1>Campagnes des compétences</h1>
                    <p>Gérez et planifiez vos campagnes du module Compétences</p>
                </div>
                <div className="sk2-header-actions">
                    <button className="sk2-btn sk2-btn--primary" type="button" onClick={openAdd}>
                        <Plus size={15} /> Ajouter une campagne
                    </button>
                </div>
            </div>

            {/* ── Toolbar ── */}
            <div className="sk2-toolbar">
                <div className="sk2-search-wrap">
                    <Search size={14} className="sk2-search-icon" />
                    <input type="text" className="sk2-search" value={tableQ} onChange={e => setTableQ(e.target.value)} placeholder="Nom, référence, année..." />
                </div>
                <div className="sk2-filter-tabs">
                    {[{ key: "all", label: "Toutes" }, { key: "active", label: "Actives" }, { key: "inactive", label: "Inactives" }].map(f => (
                        <button key={f.key} type="button" className={`sk2-filter-tab${filterStatus === f.key ? " active" : ""}`} onClick={() => setFilterStatus(f.key)}>
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="sk2-meta">
                    {campagnesLoading ? "..." : `${filteredCampagnes.length} campagne${filteredCampagnes.length !== 1 ? "s" : ""}`}
                </div>
            </div>

            {/* ── Table ── */}
            <div className="sk2-wrap">
                <div className="sk2-card">
                    <div className="sk2-body">
                        <div className="sk2-table-wrap">
                            <table className="sk2-table">
                                <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Référence</th>
                                    <th>Année</th>
                                    <th>Statut</th>
                                    <th>Validation</th>
                                    <th>Emails</th>
                                    <th>Relances</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {campagnesLoading ? (
                                    <tr><td colSpan={8}><div className="sk2-loading"><div className="sk2-spinner" /><span>Chargement...</span></div></td></tr>
                                ) : filteredCampagnes.length === 0 ? (
                                    <tr><td colSpan={8} className="sk2-empty-cell">Aucune campagne Compétences trouvée</td></tr>
                                ) : filteredCampagnes.map(c => (
                                    <tr key={c.id}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <Star size={13} style={{ color: "#ee645f", flexShrink: 0 }} />
                                                <div>
                                                    <div className="sk2-strong" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        {c.nom}
                                                        {!!c.isDefault && (
                                                            <span className="sk2-badge sk2-badge--orange" style={{ fontSize: '9px', padding: '1px 6px' }}>
                                                                DÉFAUT
                                                            </span>
                                                        )}
                                                    </div>
                                                    {c.description && (
                                                        <div className="sk2-muted" style={{ fontSize: 11, marginTop: 2 }}>
                                                            {c.description.length > 55 ? c.description.substring(0, 55) + "…" : c.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="sk2-muted">{c.reference || "—"}</td>
                                        <td><span className="sk2-badge sk2-badge--blue">{c.anneeCampagne}</span></td>
                                        <td>
                                            {c.status
                                                ? <span className="sk2-badge sk2-badge--green">Active</span>
                                                : <span className="sk2-badge sk2-badge--grey">Inactive</span>}
                                        </td>
                                        <td><span className="sk2-badge sk2-badge--blue">{c.validation || "—"}</span></td>
                                        <td><span className="sk2-badge sk2-badge--grey">{c.envoiEmails || "—"}</span></td>
                                        <td>
                                            {c.planificationRelance
                                                ? <span className="sk2-badge sk2-badge--orange">Planifiée</span>
                                                : <span className="sk2-badge sk2-badge--grey">Non</span>}
                                        </td>
                                        <td className="is-right">
                                            <div className="sk2-row-actions">
                                                <button className="sk2-icon-btn" type="button" title="Voir" onClick={() => openView(c)}><Eye size={15} /></button>
                                                <button className="sk2-icon-btn" type="button" title="Modifier" onClick={() => openEdit(c)}><Edit size={15} /></button>
                                                <button className="sk2-icon-btn sk2-icon-btn--danger" type="button" title="Supprimer" onClick={() => remove(c)}><Trash2 size={15} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <ViewModal open={viewModalOpen} campagne={selectedCampagne}
                       onClose={() => { setViewModalOpen(false); setSelectedCampagne(null); }}
                       entites={entites} />
            <FormModal open={formModalOpen} campagneId={editId} campagnes={campagnes}
                       onClose={() => setFormModalOpen(false)} onSave={handleSave}
                       isLoading={campagnesLoading} entites={entites} jobs={jobs} users={users} />
        </div>
    );
}